import { FormInstance, Modal } from 'antd';
import { RcFile } from 'antd/es/upload';
import { contentService } from 'content/service';
import { SubtitleInterface } from 'content/type';
import React from 'react';

interface SubtitleHandlersProps {
  contentId: number;
  form: FormInstance;
  setDataSource: React.Dispatch<React.SetStateAction<SubtitleInterface[]>>;
}

interface SubtitleHandlers {
  handleLanguageChange: (value: string, record: { id: string }) => void;
  handleFileChange: (info: RcFile, record: { id: string }) => void;
  handleActiveChange: (value: boolean, record: { id: string }) => void;
  handleAddSubtitle: (id: number) => void;
  handleSubtitleDownload: (subtitle: SubtitleInterface) => void;
  handleIntegratedChange: (subtitle: SubtitleInterface) => void;
  handleDeleteSubtitle: (subtitle: SubtitleInterface) => void;
}

const subtitleHandlers = ({
  form,
  contentId,
  setDataSource,
}: SubtitleHandlersProps): SubtitleHandlers => {
  const handleLanguageChange = (value: string, record: { id: string }): void => {
    setDataSource((prev: SubtitleInterface[]) => {
      const newDataSource = prev.map(subtitle =>
        subtitle.id === Number(record.id) ? { ...subtitle, lang: value } : subtitle
      );
      form.setFieldValue('subtitleList', newDataSource);
      return newDataSource;
    });
  };

  const handleFileChange = (info: RcFile, record: { id: string }): void => {
    if (info) {
      setDataSource((prev: SubtitleInterface[]) => {
        const newDataSource = prev.map(subtitle =>
          subtitle.id === Number(record.id) ? { ...subtitle, file: info } : subtitle
        );

        form.setFieldValue('subtitleList', newDataSource);
        return newDataSource;
      });
    }
  };

  const handleActiveChange = (value: boolean, record: { id: string }): void => {
    setDataSource((prev: SubtitleInterface[]) => {
      const newDataSource = prev.map(subtitle =>
        subtitle.id === Number(record.id) ? { ...subtitle, active: value } : subtitle
      );
      form.setFieldValue('subtitleList', newDataSource);
      return newDataSource;
    });
  };

  const handleAddSubtitle = (id: number): void => {
    setDataSource((prev: SubtitleInterface[]) => {
      const prevNewSubtitle = form.getFieldValue('subtitleList') || [];
      const newSubtitle = {
        id: id * -1,
        lang: '',
        file: undefined,
        created: new Date().toISOString(),
        modified: new Date().toISOString(),
        content_id: contentId,
        external_url: '',
        is_integrated: false,
      };
      form.setFieldValue('subtitleList', [...prevNewSubtitle, newSubtitle]);
      return [...prev, newSubtitle];
    });
  };

  const handleSubtitleDownload = async (subtitle: SubtitleInterface): Promise<void> => {
    try {
      // Priority: external_url -> file (RcFile object) -> error
      const fileUrl = subtitle.external_url || subtitle.file;

      if (!fileUrl) {
        throw new Error('No valid file URL found for download');
      }

      const response = await fetch(fileUrl as string);

      if (!response.ok) {
        throw new Error('File fetch failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;

      const fileName = (fileUrl as string).split('/').pop();

      link.download = fileName || 'downloaded-subtitle';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url); // Prevent memory leak
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleIntegratedChange = (record: SubtitleInterface): void => {
    setDataSource((prev: SubtitleInterface[]) => {
      const newDataSource = prev.map(subtitle =>
        subtitle.id === record.id
          ? { ...subtitle, is_integrated: !subtitle.is_integrated }
          : subtitle
      );
      console.log('newDataSource', newDataSource);
      form.setFieldValue('subtitleList', newDataSource);
      return newDataSource;
    });
  };

  const handleDeleteSubtitle = async (record: SubtitleInterface): Promise<void> => {
    Modal.confirm({
      title: 'Delete Subtitle',
      content: 'Are you sure you want to delete this subtitle?',
      onOk: async () => {
        try {
          await contentService.deleteSubtitle(contentId, record.id);
          setDataSource((prev: SubtitleInterface[]) => {
            return prev.filter(subtitle => subtitle.id !== record.id);
          });
          window.location.reload();
        } catch (error) {
          console.log(error);
        }
      },
    });
  };

  return {
    handleLanguageChange,
    handleFileChange,
    handleActiveChange,
    handleAddSubtitle,
    handleSubtitleDownload,
    handleIntegratedChange,
    handleDeleteSubtitle,
  };
};

export default subtitleHandlers;
